/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import { vincularFacturaEmitida, desvincularVinculacion } from "../../utils/conciliacionSII";
import { client } from "../../utils/supabase";
import { tieneEdificiosAsignados } from "../../utils/tieneEdificiosAsignados";

const MESES_LABELS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const formatMonto = (monto, prefijo = "$") => {
  if (monto == null) return "-";
  return `${prefijo} ${Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [, month, day] = fechaStr.split("-");
  return `${day}/${month}`;
};

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const thStyle = {
  padding: "8px",
  textAlign: "center",
  color: "#fff",
  fontSize: "0.65rem",
  textTransform: "uppercase",
  fontWeight: 600,
};

const SeleccionarDTEModal = ({ isOpen, toggle, oportunidad, onVinculada }) => {
  const { pais, id: userId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const [dtes, setDtes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(null);
  const [filtroMes, setFiltroMes] = useState(null); // null = todo el año, 1-12 = mes específico
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  const anoNum = oportunidad?.facturafecha
    ? oportunidad.facturafecha.split("-")[0]
    : oportunidad?.ano || new Date().getFullYear().toString();

  // Mes de la venta (para destacar el pill correspondiente)
  const mesOportunidad = oportunidad?.mes ? parseInt(oportunidad.mes, 10) : null;

  const cargarDTEs = useCallback(async () => {
    if (!oportunidad || !pais) return;
    setCargando(true);
    try {
      const { data } = await client
        .from("facturas_sii_emitidas")
        .select("*, facturas_sii_vinculaciones(id, venta_tipo, venta_ref_id, venta_ref_index)")
        .eq("pais", pais)
        .gte("fecha_emision", `${anoNum}-01-01`)
        .lte("fecha_emision", `${anoNum}-12-31`)
        .order("fecha_emision", { ascending: true })
        .order("folio", { ascending: true });

      setDtes(data || []);
    } catch (err) {
      console.error("Error cargando DTEs:", err);
      toast.error(`Error cargando documentos del ${enteFiscal}`);
    } finally {
      setCargando(false);
    }
  }, [oportunidad, pais, anoNum, enteFiscal]);

  useEffect(() => {
    if (isOpen && oportunidad) {
      cargarDTEs();
    }
  }, [isOpen, oportunidad, cargarDTEs]);

  useEffect(() => {
    if (isOpen) {
      setFiltroMes(null);
      setFiltroEstado("");
      setFiltroBusqueda("");
    }
  }, [isOpen]);

  // Conteo de DTEs por mes (índice 0 = enero)
  const countsByMonth = MESES_LABELS.map((_, idx) => {
    const mes = idx + 1;
    return dtes.filter((d) => {
      if (!d.fecha_emision) return false;
      return parseInt(d.fecha_emision.split("-")[1], 10) === mes;
    }).length;
  });

  // Verificar si un DTE está vinculado a ESTA oportunidad
  const estaVinculadaAEsta = (dte) => {
    const vincs = dte.facturas_sii_vinculaciones || [];
    if (!oportunidad) return false;

    // Cartelería
    if (oportunidad.negocio_id) {
      return vincs.some(
        (v) =>
          v.venta_tipo === "negocios_carteleria" &&
          String(v.venta_ref_id) === String(oportunidad.negocio_id) &&
          String(v.venta_ref_index ?? "") === String(oportunidad.sii_ref_index ?? "")
      );
    }
    // Sales
    if (oportunidad.type === "pauta") {
      return vincs.some((v) => v.venta_tipo === "sales" && String(v.venta_ref_id) === String(oportunidad.id));
    }
    // BTL
    if (oportunidad.type === "BTL") {
      return vincs.some((v) => v.venta_tipo === "sales_btl" && String(v.venta_ref_id) === String(oportunidad.id));
    }
    // Contrato
    if (oportunidad.type === "contrato") {
      return vincs.some(
        (v) =>
          v.venta_tipo === "sales_contratos" &&
          String(v.venta_ref_id) === String(oportunidad.contrato_id) &&
          String(v.venta_ref_index ?? "") === String(oportunidad.mes_index ?? "")
      );
    }
    return false;
  };

  // Derivar venta_tipo y ref_id desde la oportunidad
  const getVinculacionData = () => {
    if (oportunidad.negocio_id) {
      return {
        venta_tipo: "negocios_carteleria",
        venta_ref_id: Number(oportunidad.negocio_id),
        venta_ref_index: oportunidad.sii_ref_index ?? oportunidad.entry_index ?? null,
      };
    }
    if (oportunidad.type === "pauta") {
      return { venta_tipo: "sales", venta_ref_id: Number(oportunidad.id), venta_ref_index: null };
    }
    if (oportunidad.type === "BTL") {
      return { venta_tipo: "sales_btl", venta_ref_id: Number(oportunidad.id), venta_ref_index: null };
    }
    if (oportunidad.type === "contrato") {
      return {
        venta_tipo: "sales_contratos",
        venta_ref_id: Number(oportunidad.contrato_id),
        venta_ref_index: oportunidad.mes_index ?? null,
      };
    }
    return null;
  };

  // Verificar si este concepto ya está vinculado a ALGÚN DTE (en los datos cargados)
  const conceptoYaVinculado = () => dtes.some((dte) => estaVinculadaAEsta(dte));

  // Asegura entry de meses_facturacion para Gestión/HW-OPEX y llama a vincularFacturaEmitida
  const ejecutarVinculacion = async (dte, vincData) => {
    const esGestionOpexCart =
      oportunidad.negocio_id && (oportunidad.type === "Gestión" || oportunidad.type === "HW-OPEX");
    if (esGestionOpexCart && vincData.venta_ref_index == null) {
      const tipo = oportunidad.type === "Gestión" ? "gestion" : "opex";
      const mes = String(oportunidad.mes).padStart(2, "0");
      const ano = String(oportunidad.ano);

      const { data: negData, error: errFetch } = await client
        .from("negocios_carteleria")
        .select("meses_facturacion")
        .eq("id", oportunidad.negocio_id)
        .single();
      if (errFetch) throw errFetch;

      let mesesFact = negData?.meses_facturacion;
      if (typeof mesesFact === "string") mesesFact = JSON.parse(mesesFact || "[]");
      mesesFact = mesesFact || [];

      const existingIdx = mesesFact.findIndex((mf) => mf.mes === mes && mf.ano === ano && mf.tipo === tipo);

      if (existingIdx >= 0) {
        vincData.venta_ref_index = existingIdx;
      } else {
        const nuevoEntry = {
          mes,
          ano,
          tipo,
          facturanumero: String(dte.folio),
          facturafecha: dte.fecha_emision,
          plazocobro: null,
          facturafechacobro: null,
        };
        const nuevoArray = [...mesesFact, nuevoEntry];
        const { error: errUpdate } = await client
          .from("negocios_carteleria")
          .update({ meses_facturacion: nuevoArray })
          .eq("id", oportunidad.negocio_id);
        if (errUpdate) throw errUpdate;
        vincData.venta_ref_index = nuevoArray.length - 1;
      }
    }

    await vincularFacturaEmitida(dte.id, {
      ...vincData,
      cliente_id: oportunidad.client_id || null,
      usuario_id: userId,
    });
  };

  const handleVincular = async (dte) => {
    const vincData = getVinculacionData();
    if (!vincData) return;

    // Prevenir doble vinculación: si este concepto ya está vinculado a otro DTE, bloquear
    if (conceptoYaVinculado()) {
      toast.error("Este concepto ya está vinculado a otra factura. Desvinculá primero.");
      return;
    }

    setProcesando(dte.id);
    try {
      await ejecutarVinculacion(dte, vincData);
      const nuevaVinc = {
        id: Date.now(),
        venta_tipo: vincData.venta_tipo,
        venta_ref_id: vincData.venta_ref_id,
        venta_ref_index: vincData.venta_ref_index,
      };
      setDtes((prev) =>
        prev.map((d) =>
          d.id === dte.id
            ? {
                ...d,
                estado_vinculacion: "vinculada",
                facturas_sii_vinculaciones: [...(d.facturas_sii_vinculaciones || []), nuevaVinc],
              }
            : d
        )
      );
      toast.success(`Vinculada a folio ${dte.folio}`);
      onVinculada?.();
    } catch (err) {
      console.error("Error vinculando:", err);
      toast.error(err?.code === "EDIFICIOS_FALTANTES" ? err.message : "Error al vincular");
    } finally {
      setProcesando(null);
    }
  };

  const handleDesvincular = async (dte) => {
    const vincData = getVinculacionData();
    if (!vincData) return;
    setProcesando(dte.id);
    try {
      await desvincularVinculacion(dte.id, vincData);
      // Actualizar estado local sin recargar
      setDtes((prev) =>
        prev.map((d) => {
          if (d.id !== dte.id) return d;
          const nuevasVincs = (d.facturas_sii_vinculaciones || []).filter(
            (v) =>
              !(
                v.venta_tipo === vincData.venta_tipo &&
                String(v.venta_ref_id) === String(vincData.venta_ref_id) &&
                String(v.venta_ref_index ?? "") === String(vincData.venta_ref_index ?? "")
              )
          );
          return {
            ...d,
            facturas_sii_vinculaciones: nuevasVincs,
            estado_vinculacion: nuevasVincs.length === 0 ? "pendiente" : d.estado_vinculacion,
          };
        })
      );
      toast.success(`Desvinculada de folio ${dte.folio}`);
      onVinculada?.();
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular");
    } finally {
      setProcesando(null);
    }
  };

  // Reasigna un DTE ya vinculado (a otra oportunidad o vía conciliación bancaria) hacia
  // la oportunidad actual. Limpia ambas fuentes — la 1:N y la columna estado_vinculacion
  // de facturas_sii_emitidas — y luego ejecuta el flujo normal de vinculación.
  const handleEditar = async (dte) => {
    const vincData = getVinculacionData();
    if (!vincData) return;

    if (conceptoYaVinculado()) {
      toast.error("Este concepto ya está vinculado a otra factura. Desvinculá primero.");
      return;
    }

    // Pre-check fail-fast (protege el orden del re-vínculo): handleEditar suelta el folio viejo
    // ANTES de vincular el nuevo. Si el destino no tiene edificios, el guard de
    // vincularFacturaEmitida frenaría recién en el re-vínculo, dejando la venta vieja sin folio.
    // Validamos acá, antes del loop de desvincular, para abortar sin tocar nada. Mismo predicado
    // y mensaje que el guard SII (regla de oro); solo aplica a sales lineal y cuotas de contrato.
    if (oportunidad.type === "contrato" && !tieneEdificiosAsignados(oportunidad.edificios_ids)) {
      toast.error(
        `No se puede vincular: la cuota de ${oportunidad.mes}-${oportunidad.ano} no tiene edificios asignados. Asigná los edificios antes de vincular la factura.`
      );
      return;
    }
    if (oportunidad.type === "pauta" && !tieneEdificiosAsignados(oportunidad.edificiosparticipantes)) {
      toast.error(
        "No se puede vincular: la venta no tiene edificios asignados. Asigná los edificios antes de vincular la factura."
      );
      return;
    }

    setProcesando(dte.id);
    try {
      // 1. Borrar cada fila de la 1:N (desvincularVinculacion también revierte la columna
      //    a 'pendiente' cuando no quedan vinculaciones)
      for (const v of dte.facturas_sii_vinculaciones || []) {
        await desvincularVinculacion(dte.id, {
          venta_tipo: v.venta_tipo,
          venta_ref_id: v.venta_ref_id,
          venta_ref_index: v.venta_ref_index,
        });
      }
      // 2. Reset adicional para el caso desincronizado: 1:N vacía pero estado_vinculacion='vinculada'
      //    (vinculaciones creadas por conciliación bancaria pre-fix). Idempotente con paso 1.
      await client
        .from("facturas_sii_emitidas")
        .update({
          estado_vinculacion: "pendiente",
          venta_tipo: null,
          venta_ref_id: null,
          venta_ref_index: null,
          cliente_id: null,
          vinculado_por: null,
          vinculado_at: null,
        })
        .eq("id", dte.id);

      // 3. Vincular a la oportunidad actual con el flujo estándar
      await ejecutarVinculacion(dte, vincData);
      const nuevaVinc = {
        id: Date.now(),
        venta_tipo: vincData.venta_tipo,
        venta_ref_id: vincData.venta_ref_id,
        venta_ref_index: vincData.venta_ref_index,
      };
      setDtes((prev) =>
        prev.map((d) =>
          d.id === dte.id ? { ...d, estado_vinculacion: "vinculada", facturas_sii_vinculaciones: [nuevaVinc] } : d
        )
      );
      toast.success(`Reasignada a folio ${dte.folio}`);
      onVinculada?.();
    } catch (err) {
      console.error("Error reasignando:", err);
      toast.error(err?.code === "EDIFICIOS_FALTANTES" ? err.message : "Error al reasignar");
    } finally {
      setProcesando(null);
    }
  };

  if (!oportunidad) return null;

  // Estilos compartidos para pills
  const pillBase = {
    padding: "3px 9px",
    fontSize: "0.68rem",
    fontWeight: 600,
    border: "1px solid #dee2e6",
    borderRadius: 4,
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" style={{ maxWidth: 1300 }}>
      <ModalBody>
        {/* Datos de la venta */}
        <div
          style={{
            background: "#000",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.85rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            justifyContent: "center",
          }}
        >
          <span>
            <strong>Venta:</strong> {(oportunidad.cliente || "").toUpperCase()}
          </span>
          <span>
            <strong>Tipo:</strong> {oportunidad.type}
          </span>
          <span>
            <strong>Mes venta:</strong>{" "}
            {mesOportunidad ? `${MESES_LABELS[mesOportunidad - 1]}/${String(anoNum).slice(-2)}` : "-"}
          </span>
          <span>
            <strong>Importe:</strong> {formatMonto(oportunidad.importe, oportunidad.moneda_dolar ? "US$" : "$")}
          </span>
          <span>
            <strong>Factura:</strong> {oportunidad.facturanumero || "Sin facturar"}
          </span>
        </div>

        {/* GRID compartido: ROW 1 (meses) + ROW 2 (estado + búsqueda) — mismas columnas */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(13, 1fr)",
            gap: 4,
            marginBottom: 12,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {/* ── ROW 1 ── */}
          <button
            type="button"
            onClick={() => setFiltroMes(null)}
            style={{
              ...pillBase,
              background: filtroMes === null ? "#2c3e50" : "#f0f2f4",
              color: filtroMes === null ? "#fff" : "#555",
              borderColor: filtroMes === null ? "#2c3e50" : "#dee2e6",
              whiteSpace: "nowrap",
            }}
          >
            Todo {anoNum}
          </button>

          {MESES_LABELS.map((label, idx) => {
            const mes = idx + 1;
            const count = countsByMonth[idx];
            const activo = filtroMes === mes;
            const esMesVenta = mes === mesOportunidad;
            const disabled = count === 0;
            return (
              <button
                key={mes}
                type="button"
                onClick={() => !disabled && setFiltroMes(activo ? null : mes)}
                disabled={disabled}
                title={esMesVenta ? "Mes de la venta" : undefined}
                style={{
                  ...pillBase,
                  flex: 1,
                  minWidth: 0,
                  textAlign: "center",
                  background: activo ? "#2c3e50" : "transparent",
                  color: activo ? "#fff" : disabled ? "#ccc" : "#444",
                  borderColor: activo ? "#2c3e50" : esMesVenta ? "#e67e22" : disabled ? "#eee" : "#dee2e6",
                  borderWidth: esMesVenta && !activo ? 2 : 1,
                  cursor: disabled ? "default" : "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {label}
                {count > 0 && (
                  <span
                    style={{
                      marginLeft: 4,
                      background: activo ? "rgba(255,255,255,0.25)" : esMesVenta ? "#e67e22" : "#e9ecef",
                      color: activo || esMesVenta ? "#fff" : "#555",
                      borderRadius: 10,
                      padding: "0 5px",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}

          {/* ── ROW 2 ── */}
          {/* Todas, Pendiente, Vinculada — ocupan 1 columna cada uno, igual que un pill */}
          {[
            { value: "", label: "Todas" },
            { value: "pendiente", label: "Pendiente" },
            { value: "vinculada", label: "Vinculada" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFiltroEstado(opt.value)}
              style={{
                ...pillBase,
                textAlign: "center",
                background: filtroEstado === opt.value ? "#2c3e50" : "transparent",
                color: filtroEstado === opt.value ? "#fff" : "#666",
                borderColor: filtroEstado === opt.value ? "#2c3e50" : "#dee2e6",
              }}
            >
              {opt.label}
            </button>
          ))}

          {/* Búsqueda ocupa las 10 columnas restantes */}
          <input
            type="text"
            placeholder="Buscar razón social o folio..."
            value={filtroBusqueda}
            onChange={(e) => setFiltroBusqueda(e.target.value)}
            style={{
              gridColumn: "span 10",
              padding: "4px 10px",
              fontSize: "0.75rem",
              border: "1px solid #dee2e6",
              borderRadius: 4,
            }}
          />
        </div>

        {cargando && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>Cargando documentos del {enteFiscal}...</div>
        )}

        {!cargando && dtes.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
            No hay documentos del {enteFiscal} para este período.
          </div>
        )}

        {!cargando &&
          dtes.length > 0 &&
          (() => {
            const dtesFiltrados = dtes.filter((dte) => {
              // Filtro por mes
              if (filtroMes !== null) {
                if (!dte.fecha_emision) return false;
                if (parseInt(dte.fecha_emision.split("-")[1], 10) !== filtroMes) return false;
              }
              // Filtro por estado — combina ambas fuentes (1:N + columna estado_vinculacion)
              // para cubrir DTEs vinculados vía conciliación bancaria pre-fix
              if (filtroEstado === "pendiente") {
                const vincs = dte.facturas_sii_vinculaciones || [];
                if (vincs.length > 0) return false;
                if (dte.estado_vinculacion === "vinculada") return false;
              }
              if (filtroEstado === "vinculada") {
                const vincs = dte.facturas_sii_vinculaciones || [];
                if (vincs.length === 0 && dte.estado_vinculacion !== "vinculada") return false;
              }
              // Filtro por búsqueda
              if (filtroBusqueda) {
                const texto = filtroBusqueda.toLowerCase();
                const nombre = (dte.razon_social_receptor || "").toLowerCase();
                if (!nombre.includes(texto) && !String(dte.folio).includes(texto)) return false;
              }
              return true;
            });

            if (dtesFiltrados.length === 0) {
              return (
                <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
                  No hay documentos que coincidan con los filtros.
                </div>
              );
            }

            return (
              <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "27%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "13%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#2c3e50" }}>
                    <th style={thStyle}>Folio</th>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>RUT</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>Razón Social</th>
                    <th style={thStyle}>Neto</th>
                    <th style={thStyle}>Total</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {dtesFiltrados.map((dte) => {
                    const vinculadaAEsta = estaVinculadaAEsta(dte);
                    // Vinculada a otra oportunidad — incluye casos desincronizados donde la 1:N
                    // está vacía pero la columna estado_vinculacion dice 'vinculada' (conciliación bancaria pre-fix)
                    const vinculadaAOtra =
                      !vinculadaAEsta &&
                      ((dte.facturas_sii_vinculaciones || []).length > 0 || dte.estado_vinculacion === "vinculada");

                    return (
                      <tr key={dte.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: 600 }}>{dte.folio}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>{formatFecha(dte.fecha_emision)}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>{formatRut(dte.rut_receptor)}</td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={dte.razon_social_receptor}
                        >
                          {dte.razon_social_receptor}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {formatMonto(dte.monto_neto || dte.monto_exento)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right", fontWeight: 600 }}>
                          {formatMonto(dte.monto_total)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {vinculadaAEsta ? (
                            <span style={{ color: "#27ae60", fontWeight: 600, fontSize: "0.7rem" }}>Vinculada ✓</span>
                          ) : vinculadaAOtra ? (
                            <span style={{ color: "#e67e22", fontSize: "0.7rem" }}>Ya vinculada</span>
                          ) : (
                            <span style={{ color: "#999", fontSize: "0.7rem" }}>
                              {(dte.estado_vinculacion || "").charAt(0).toUpperCase() +
                                (dte.estado_vinculacion || "").slice(1)}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {vinculadaAEsta ? (
                            <button
                              onClick={() => handleDesvincular(dte)}
                              disabled={procesando === dte.id}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "1px solid #c0392b",
                                borderRadius: 4,
                                background: "transparent",
                                color: "#c0392b",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              Desvincular
                            </button>
                          ) : vinculadaAOtra ? (
                            <button
                              onClick={() => handleEditar(dte)}
                              disabled={procesando === dte.id || conceptoYaVinculado()}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "1px solid #e67e22",
                                borderRadius: 4,
                                background: conceptoYaVinculado() ? "#bdc3c7" : "transparent",
                                color: conceptoYaVinculado() ? "#fff" : "#e67e22",
                                cursor: conceptoYaVinculado() ? "not-allowed" : "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {procesando === dte.id ? "..." : "Editar"}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleVincular(dte)}
                              disabled={procesando === dte.id || conceptoYaVinculado()}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "none",
                                borderRadius: 4,
                                background: conceptoYaVinculado() ? "#bdc3c7" : "#2c3e50",
                                color: "#fff",
                                cursor: conceptoYaVinculado() ? "not-allowed" : "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {procesando === dte.id ? "..." : "Vincular"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SeleccionarDTEModal;
